import api from './api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export enum ArticleStatus {
  DRAFT = "draft",
  SUBMITTED = "submitted",
  UNDER_REVIEW = "under_review",
  REJECTED = "rejected",
  ACCEPTED = "accepted",
  PUBLISHED = "published"
}

export type PdfFile = {
  file?: string;
  path?: string;
};

export interface Article {
  id: string;
  title: string;
  abstract: string;
  keywords: string[];
  doi?: string;
  edn?: string;
  status: ArticleStatus;
  views: number;
  downloads: number;
  citations: number;
  authors?: {
    id: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    email: string;
    role: string;
  }[];
  issue?: {
    id: number;
    title: string;
    description: string;
    volume: number;
    number: number;
    year: number;
    status: string;
  };
  pdfFile?: PdfFile;
  pages?: string;
  submittedDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ArticlesResponse {
  articles: Article[];
  total: number;
  page: number;
  limit: number;
}

export interface ArticlesFilters {
  search?: string;
  category?: string;
  year?: string;
  sortBy?: string;
  page?: number;
  limit?: number;
}

// Тип для данных статьи при создании
export interface CreateArticleData {
  title: string;
  abstract: string;
  keywords: string[];
  authors: string[];
  doi?: string;
  status: ArticleStatus;
  issueId?: number;
  pdfFile?: PdfFile;
}

// Тип для данных статьи при обновлении
export interface UpdateArticleData {
  title?: string;
  abstract?: string;
  keywords?: string[];
  authors?: string[];
  doi?: string;
  status?: ArticleStatus;
  issueId?: number;
  pdfFile?: PdfFile;
}

export const articleService = {
  // Получение списка статей с фильтрацией и пагинацией
  getArticles: async (filters: ArticlesFilters): Promise<ArticlesResponse> => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.year) params.append('year', filters.year);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    console.log('Отправка запроса на получение статей:', `${API_URL}/articles?${params.toString()}`);
    try {
      const response = await api.get<ArticlesResponse>(`/articles?${params.toString()}`);
      console.log('Ответ от сервера:', response.data);
      return response.data;
    } catch (error) {
      console.error('Ошибка при получении статей:', error);
      throw error;
    }
  },

  // Получение статьи по ID
  getArticleById: async (id: string): Promise<Article> => {
    const response = await api.get<Article>(`/articles/${id}`);
    return response.data;
  },

  // Создание новой статьи
  createArticle: async (formData: FormData): Promise<Article> => {
    try {
      const response = await api.post<Article>('/articles', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Ошибка при создании статьи:', {
        error,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Обновление статьи
  updateArticle: async (id: string, formData: FormData): Promise<Article> => {
    try {
      const response = await api.put<Article>(`/articles/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Ошибка при обновлении статьи:', {
        error,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Удаление статьи
  deleteArticle: async (id: string): Promise<void> => {
    try {
      await api.delete(`/articles/${id}`);
    } catch (error) {
      console.error('Ошибка при удалении статьи:', error);
      throw error;
    }
  },

  // Получение всех статей для админ панели
  getAllArticlesAdmin: async (): Promise<Article[]> => {
    try {
      const response = await api.get<Article[]>('/articles/admin/all');
    return response.data;
    } catch (error) {
      console.error('Ошибка при получении статей для админ панели:', error);
      throw error;
    }
  },

  // Получение статей конкретного автора
  getAuthorArticles: async (authorId: string): Promise<Article[]> => {
    try {
      const response = await api.get<Article[]>(`/articles/author/${authorId}`);
      return response.data;
    } catch (error) {
      console.error('Ошибка при получении статей автора:', error);
      throw error;
    }
  },

  // Публикация статьи
  publishArticle: async (id: string): Promise<Article> => {
    try {
    const response = await api.post<Article>(`/articles/${id}/publish`);
    return response.data;
    } catch (error) {
      console.error('Ошибка при публикации статьи:', error);
      throw error;
    }
  },

  // Удаление PDF файла статьи
  deletePdfFile: async (id: string): Promise<Article> => {
    try {
      console.log('Отправляем запрос на удаление PDF файла для статьи:', id);
      const response = await api.delete<Article>(`/articles/${id}/pdf`);
      console.log('Ответ от сервера при удалении PDF:', response.data);
      return response.data;
    } catch (error) {
      console.error('Ошибка при удалении PDF файла:', error);
      throw error;
    }
  },

  // Увеличение счетчика просмотров
  incrementViews: async (id: string): Promise<Article> => {
    try {
      const response = await api.post<Article>(`/articles/${id}/views`);
      return response.data;
    } catch (error) {
      console.error('Ошибка при увеличении счетчика просмотров:', error);
      throw error;
    }
  },

  // Увеличение счетчика скачиваний
  incrementDownloads: async (id: string): Promise<Article> => {
    try {
      const response = await api.post<Article>(`/articles/${id}/downloads`);
      return response.data;
    } catch (error) {
      console.error('Ошибка при увеличении счетчика скачиваний:', error);
      throw error;
    }
  },

  // Скачивание PDF файла статьи
  downloadPdf: async (filePath: string): Promise<Blob> => {
    try {
      const response = await api.get<Blob>(`/uploads/${filePath}`, {
        responseType: 'blob'
      });
      return response.data as Blob;
    } catch (error) {
      console.error('Ошибка при скачивании PDF файла:', error);
      throw error;
    }
  },

  // Функция для создания ЧПУ
  createSlug: (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^а-яёa-z0-9\s-]/g, '') // Удаляем все кроме букв, цифр и пробелов
      .replace(/[ё]/g, 'e') // Заменяем ё на e
      .replace(/[а-я]/g, (match) => {
        const translit: { [key: string]: string } = {
          'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e',
          'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l',
          'м': 'm', 'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's',
          'т': 't', 'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch',
          'ш': 'sh', 'щ': 'sch', 'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e',
          'ю': 'yu', 'я': 'ya'
        };
        return translit[match] || match;
      })
      .replace(/\s+/g, '-') // Заменяем пробелы на дефисы
      .replace(/-+/g, '-') // Убираем множественные дефисы
      .replace(/^-+|-+$/g, ''); // Убираем дефисы в начале и конце
  }
}; 